import jQuery from "jquery";
import View from "../framework/view";
import Server from "../server";
import {Recording} from "../bo/recording";
import template from "../../pugc/player";
import {Balance} from "../bo/balance";
import {hashUrlToUrl, plural, renderCredits, unlimitedCredits} from "../misc/utils";
import {Role, ROLE_ADMIN, ROLE_EDITOR} from "../misc/types";
import Alerts from "../misc/alerts";
import {User} from "../bo/user";

// Loaded via <script> tag, create shortcut to access PDF.js exports.
// @ts-ignore
const { pdfjsLib } = globalThis;

// The workerSrc property shall be specified.
pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.3.136/pdf.worker.mjs';

let videoid = 0;

// Global list for disposal - this implementation implies that we can only have one player at a time.
const players : any[] = [];

// Global space bar handler
let spaceBarHandler : any = undefined;

jQuery(document).on("keyup keydown", function(e) {
    if (e.key == " ") {
        spaceBarHandler && spaceBarHandler(e);
    }
});

function renderTimestamp(x: number) {
    const minutes = Math.floor(x / 60);
    const seconds = Math.floor(x - minutes * 60);
    const tenths = Math.floor((x - minutes * 60 - seconds) * 10);

    return minutes + ":" + String(seconds).padStart(2, "0") + "." + String(tenths).padStart(1, "0");
}

export default class PlayerView extends View {

    public static classname = "player-view";

    private server = Server.instance();

    private id: number;

    private recording: Recording | undefined;

    private balance: Balance | undefined;

    private player : any;

    private marks : number[] = [];

    private looping : boolean = false;
    private start: number = 0;
    private end: number = 0;

    private redraw : any = undefined;

    private repitching: boolean = false;
    private repitchCents = 0;
    private repitchCentsBase = 0;

    private user: User | undefined;
    private role: Role;

    private autoplay: boolean = false;

    private leftright = 0;

    private configPanning? : () => void;

    constructor(id: number, user: User | undefined, role: Role, autoplay: boolean) {
        super(PlayerView.classname);
        this.id = id;
        this.user = user;
        this.role = role;
        this.autoplay = autoplay;
        this.refresh();
    }

    sortMarks() {
        this.marks = this.marks.sort((a, b) => { return a - b; });
        if (this.recording) {
            this.server.saveMarks(this.recording, this.marks);
        }
    }

    refresh() {
        this.server.fetchRecording(this.id).then((recording) => {
            this.recording = recording;
            this.marks = this.recording.marks || [];
            this.repitchCentsBase = this.repitchCents = this.recording.repitch_cents || 0;
            this.setRecordingOnPlayer();
            this.update();
            this.config();
        });
        this.server.getBalance().then(balance => {
            this.balance = balance;
            this.update(".purchase-option");
        });
    }

    protected render0(): JQuery<HTMLElement> {

        spaceBarHandler = (event: JQuery.TriggeredEvent<Document>) => {
            const e = this.elt();
            const p = this.player;
            if (e.find(".edit-marks-container").is(":visible")) {
                return;
            }
            if (! e.is(":visible")) {
                return;
            }
            if (p) {
                event.stopPropagation();
                event.preventDefault();
                if (event.type == "keyup") {
                    p.paused() ? p.play() : p.pause();
                    this.config();
                  }
            }
        };

        const data : any = {
            left_right_controls: this.recording && this.recording.left_right,
            recording: this.recording,
            balance: this.balance,
            marks: this.marks,
            renderTimestamp: renderTimestamp,
            renderCredits: renderCredits,
            unlimitedCredits: unlimitedCredits,
            repitchCents: this.repitchCents,
            plural: plural,
            user: this.user,
            admin: this.role == ROLE_ADMIN,
            editor: this.role == ROLE_EDITOR,
        }

        const token = this.recording && this.recording.token;
        if (token) {
            data.review_url = hashUrlToUrl(`/review/${token}`);
        }

        const out = this.pug(template, data);

        this.afterUpdate(() => {

            const v = this.elt().find("video-js");

            if (v.length == 1 && ! v.data("initted")) {

                const id = this.getUuid() + "-video-" + (++videoid);
                v.attr("id", id);
                v.data("initted", true);

                const configOptions = (newOpts: any) => {
                    this.elt().find(".loop-a").text(newOpts.start ? "A " + Math.round(newOpts.start * 10) / 10 : "A");
                    this.elt().find(".loop-b").text(newOpts.end ? "B " + Math.round(newOpts.end * 10) / 10 : "B");
                }

                // @ts-ignore
                this.player = videojs(v[0], {
                    preload: true,
                    fluid: true,
                    controls: true,
                    playbackRates: [0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
                    plugins: {
                        abLoopPlugin: {
                            createButtons: true,
                            onOptionsChange: function (optionDetails: any, api: any, player: any) {
                                console.log("options changed: ", player.id(), optionDetails);
                                configOptions(optionDetails.newOpts);
                            }
                        }
                    }
                });

                this.player.crossOrigin = "anonymous";

                const videoElt = this.elt().find("video");
                const vid = videoElt[0] as HTMLVideoElement;
                vid.crossOrigin = "anonymous";

                if (this.recording) {
                    if (this.recording.left_right) {
                        this.setupLeftRight();
                    }  else {
                        this.setupMono();
                    }
                }

                for (const p of players) {
                    p.dispose();
                }
                players.length = 0;

                players.push(this.player);

                if (this.recording) {
                    this.setRecordingOnPlayer();
                }

                this.player.on(["play", "seeked", "pause", "ready", "durationchange"], () => {
                    this.config();
                });

            }

            this.showLeadSheet();
        });

        return out;
    }

    setupLeftRight() {

        const videoElt = this.elt().find("video");
        const vid = videoElt[0] as HTMLVideoElement;

        const audioCtx = new AudioContext({latencyHint: "interactive"});
        console.log(`baseLatency=${audioCtx.baseLatency}`);
        
        const source = audioCtx.createMediaElementSource(vid);
        source.channelInterpretation = "discrete";

        const gainL = audioCtx.createGain();
        const gainR = audioCtx.createGain();

        const gainLL = audioCtx.createGain();
        const gainLR = audioCtx.createGain();
        const gainRL = audioCtx.createGain();
        const gainRR = audioCtx.createGain();

        const merger = new ChannelMergerNode(audioCtx, {
            numberOfInputs: 2,
            channelInterpretation: "discrete"
        });
        const splitter = new ChannelSplitterNode(audioCtx, {
            numberOfOutputs: 2,
            channelInterpretation: "discrete"
        });

        // Connect to source
        // Connect the source to the splitter
        source.connect(splitter, 0, 0);

        splitter.connect(gainL, 0);
        splitter.connect(gainR, 1);

        gainL.connect(gainLL);
        gainL.connect(gainLR);

        gainR.connect(gainRL);
        gainR.connect(gainRR);

        const gain = audioCtx.createGain();
        if (this.recording && this.recording.db_adjust != null) {
            gain.gain.value = Math.pow(10, this.recording.db_adjust / 20);
        }

        merger.connect(gain, 0, 0);
        gain.connect(audioCtx.destination, 0, 0);

        gainLL.connect(merger, 0, 0);
        gainLR.connect(merger, 0, 1);
        gainRL.connect(merger, 0, 0);
        gainRR.connect(merger, 0, 1);

        gainLL.gain.value = 0.6;
        gainLR.gain.value = 0.4;
        gainRL.gain.value = 0.6;
        gainRR.gain.value = 0.4;

        this.configPanning = () => {

            const v = this.leftright || 0;
            const v0 = Math.abs(v);
            const g = (1 - v0);

            if (v < 0) {
                gainL.gain.value = g;
                gainR.gain.value = 1;
            } else if (v > 0) {
                gainL.gain.value = 1;
                gainR.gain.value = g;
            } else {
                gainL.gain.value = 1;
                gainR.gain.value = 1;
            }

        };

        this.configPanning();
    }

    setupMono() {

        const videoElt = this.elt().find("video");
        const vid = videoElt[0] as HTMLVideoElement;

        const audioCtx = new AudioContext({latencyHint: "interactive"});
        console.log(`baseLatency=${audioCtx.baseLatency}`);

        const source = audioCtx.createMediaElementSource(vid);
        source.channelInterpretation = "discrete";

        const gain = audioCtx.createGain();

        if (this.recording && this.recording.db_adjust != null) {
            gain.gain.value = Math.pow(10, this.recording.db_adjust / 20);
        }

        source.connect(gain, 0, 0);

        gain.connect(audioCtx.destination, 0, 0);

    }

    async showLeadSheet() {

        if (! this.recording || ! this.recording.pages) {
            return;
        }
        const url = await this.server.getFakebookUrl();
        if (! url) {
            return;
        }

        let canvas0 = this.elt().find(".leadsheet canvas");
        if (canvas0.data("recordingid") == this.recording.id) {
            return;
        }

        canvas0.remove();
        canvas0 = jQuery("<canvas>").appendTo(this.elt().find(".leadsheet"));

        canvas0.data("recordingid", this.recording.id as number);

        const pagenum = this.recording.pages.replace(/[,-].*/, "");

        const fakebook = pdfjsLib.getDocument(url);

        const pdf = await fakebook.promise;

        const page = await pdf.getPage(+pagenum);

        const scale = 1.5;
        const viewport = page.getViewport({ scale: scale, dontFlip: false });
        const outputScale = window.devicePixelRatio || 1;

        const canvas = canvas0[0] as HTMLCanvasElement;

        const context = canvas.getContext("2d");

        if (! context) {
            return;
        }

        canvas.width = Math.floor(viewport.width * outputScale);
        canvas.height = Math.floor(viewport.height * outputScale);
        canvas.style.width = Math.floor(viewport.width) + "px";
        canvas.style.height =  Math.floor(viewport.height) + "px";

        const transform = outputScale !== 1
            ? [outputScale, 0, 0, outputScale, 0, 0]
            : null;

        const renderContext = {
            canvasContext: context,
            transform: transform as any[] | undefined,
            viewport: viewport
        };
        page.render(renderContext);
    }

    drawCanvas() {

        const canvas = this.elt().find("canvas");
        if (canvas.length == 0) {
            return;
        }

        const ctx = canvas[0].getContext("2d");
        if (! ctx) {
            return;
        }

        const width = canvas[0].width;
        const height = canvas[0].height;

        ctx.fillStyle = "rgb(255 255 255)";
        ctx.fillRect(0, 0, width, height);

        if (! this.player) {
            return;
        }

        const pos = this.player.currentTime();
        const duration = this.player.duration();

        const looping = this.looping;
        if (looping) {
            ctx.fillStyle = "#a0a0a0";
            ctx.fillRect(this.start / duration * width, 0, (this.end - this.start) / duration * width, height);
        }

        ctx.fillStyle = "rgb(255 0 0)";
        for (const mark of this.marks) {
            ctx.fillRect(mark / duration * width, 0, 1, height);
        }

        const x = pos / duration * width;

        ctx.fillStyle = "rgb(0 0 0)";
        ctx.fillRect(x, 0, 1, height);

        if (! this.player.paused()) {
            if (this.redraw) {
                window.cancelAnimationFrame(this.redraw);
            }
            this.redraw = window.requestAnimationFrame(() => {
                this.drawCanvas();
            });
        }
    }

    setRecordingOnPlayer() {
        if (this.recording && this.player) {

            this.leftright = 0;

            console.log("Set recording", this.recording);

            this.player.src({
                src: this.recording.url,
                type: "video/mp4"
            });
            this.looping = false;
            const p = this.player && this.player.abLoopPlugin;
            if (p) {
                p.disable();
            }
            this.config();

            if (this.autoplay) {
                this.player.ready(() => {
                    this.play();
                });
                this.player.on("ended", () => {
                    if (this.recording && this.recording.library) {
                        this.server.fetchRecordings(this.recording.library).then((rs) => {
                            const idx = Math.floor(Math.random() * rs.length);
                            const id = rs[idx].id;
                            window.location.href = `#/player/${id}/autoplay`;
                        });
                    }
                });
            }
        }
    }

    loop() {
        const p = this.player && this.player.abLoopPlugin;
        if (! p) {
            this.looping = false;
        } else {
            this.looping = ! this.looping;
            if (this.looping) {
                this.setLoop();
                this.looping = true;
                p.enable();
            } else {
                p.disable();
            }
        }
        this.config();
    }

    setLoop() {
        const p = this.player && this.player.abLoopPlugin;
        if (! p) {
            return;
        }
        const pos = this.player.currentTime();
        const duration = this.player.duration();

        const marks = [ 0, ...this.marks, duration ];

        for (let i = 0; i < marks.length - 1; i++) {
            if (marks[i] <= pos && marks[i+1] >= pos) {
                p.setStart(marks[i]);
                p.setEnd(marks[i+1]);
                this.start = marks[i];
                this.end = marks[i+1];
                return;
            }
        }
    }

    config() {
        const playing = this.player && ! this.player.paused();
        const e = this.elt();
        e.find("button.loop").toggleClass("looping", this.looping);
        e.find("button.add-mark").prop("disabled", ! this.player || this.player.paused());
        e.find("button.edit-marks").prop("disabled", this.marks.length  == 0);
        e.find("button.play").prop("disabled", playing);
        e.find("button.pause").prop("disabled", ! playing);
        e.find("button.repitch").prop("disabled", this.repitching);
        e.find(".repitch-container").toggle(this.repitching);
        this.drawCanvas();
    }

    async purchase() {

        if (! this.recording) {
            return;
        }

        const YES = "Yes, add";

        const unlimited = this.balance && unlimitedCredits(this.balance.credits);

        const buy = () => {
            if (this.recording) {
                this.server.addRecording(this.recording).then(() => {
                    this.refresh();
                });
            }
        };

        if (unlimited) {
            buy();
        } else {
            const r = await Alerts.confirmation({
                title: `Add to library`,
                message: `Really add to library? This will use ${renderCredits(this.recording.price || 0)}.`,
                options: [ YES, "No, don't add"]
            });
            if (r == YES && this.recording) {
                buy();
            }
        }
    }

    addMark() {
        if (! this.player) {
            return;
        }

        const pos = this.player.currentTime();

        if (this.marks.filter(x => x == pos).length > 0) {
            return;
        }

        this.marks.push(+pos);
        this.sortMarks();

        this.config();

        this.update(".edit-marks-list");
    }

    editMarks(show: boolean) {
        if (show) {
            this.elt().find(".edit-marks-container").show();
        } else {

            const inputs = this.elt().find(".edit-marks-container .edit-marks-list input.mark-value");
            this.marks.length = 0;
            inputs.each((idx, elt) => {
                const val = jQuery(elt).val() as string;
                if (val.length > 0) {
                    const bits = val.split(/:/);
                    let x = 0;
                    let y;
                    while ((y = bits.shift()) !== undefined) {
                        x = x * 60 + +y;
                    }
                    if (x) {
                        this.marks.push(x);
                    }
                }
            });

            this.sortMarks();

            this.setLoop();
            this.elt().find(".edit-marks-container").hide();
            this.update(".edit-marks-list");
            this.config();
        }
    }

    seekX(x: number) {
        const canvas = this.elt().find(".marks canvas")[0];
        if ( !this.player || ! canvas) {
            return;
        }
        const rect = canvas.getBoundingClientRect();
        const duration = this.player.duration();
        const pos = (x - rect.left) / rect.width * duration;
        this.player.currentTime(pos);
    }

    rewind() {
        const pos = this.looping ? this.start : 0;
        this.player && this.player.currentTime(pos);
    }
    play() {
        this.player && this.player.play();
        this.config();
    }
    pause() {
        this.player && this.player.pause();
        this.config();
    }

    repitch() {
        this.repitching = true;
        this.config();
        const vid = this.elt().find("video");
        vid[0].preservesPitch = false;
    }

    cancelRepitch() {
        this.repitching = false;
        this.config();
        const vid = this.elt().find("video");
        vid[0].preservesPitch = true;
    }

    saveRepitch() {
        if (! this.recording) {
            return;
        }
        this.repitching = false;
        this.config();
        this.server.saveRepitch(this.recording, +this.repitchCents).then(() => {
            this.refresh();
        });
    }

    setRepitchCents(cents: number) {
        this.repitchCents = cents;
        if (! this.repitching) {
            return;
        }
        const rate = Math.exp(((+cents - this.repitchCentsBase) / 1200) * Math.log(2));
        const vid = this.elt().find("video");
        vid[0].playbackRate = rate;
    }

    async setActivation(active: boolean) {
        if (! this.recording) {
            return;
        }
        const done = await this.server.setActivation(this.recording, active);
        this.refresh();
    }

    editRecording() {
        if (! this.recording) {
            return;
        }
        const url = `#/upload/${this.recording.id}`;
        window.location.href = url;
    }

    configLeftRight(val?: number) {
        if (val !== undefined) {
            this.leftright = val;
        }
        this.configPanning && this.configPanning();
    }
}

View.on(
    "." + PlayerView.classname,
    "click",
    ".add-recording",
    function(that: PlayerView, event, elt) {
        event.preventDefault();
        that.purchase();
    });

View.on(
    "." + PlayerView.classname,
    "click",
    ".add-mark",
    function(that: PlayerView, event, elt) {
        event.preventDefault();
        that.addMark();
    });

View.on(
    "." + PlayerView.classname,
    "click",
    ".loop",
    function(that: PlayerView, event, elt) {
        event.preventDefault();
        that.loop();
    });

View.on(
    "." + PlayerView.classname,
    "click",
    ".edit-marks",
    function(that: PlayerView, event, elt) {
        event.preventDefault();
        that.editMarks(true);
    });

View.on(
    "." + PlayerView.classname,
    "click",
    ".edit-marks-container .done",
    function(that: PlayerView, event, elt) {
        event.preventDefault();
        that.editMarks(false);
    });

View.on(
    "." + PlayerView.classname,
    "click",
    "button.rewind",
    function(that: PlayerView, event, elt) {
        event.preventDefault();
        that.rewind();
    });

View.on(
    "." + PlayerView.classname,
    "click",
    "button.play",
    function(that: PlayerView, event, elt) {
        event.preventDefault();
        that.play();
    });
View.on(
    "." + PlayerView.classname,
    "click",
    "button.pause",
    function(that: PlayerView, event, elt) {
        event.preventDefault();
        that.pause();
    });

View.on(
    "." + PlayerView.classname,
    "click",
    ".marks canvas",
    function(that: PlayerView, event, elt) {
        event.preventDefault();
        that.seekX(event.clientX);
    });

View.on(
    "." + PlayerView.classname,
    "click",
    ".edit-marks-list a.delete",
    function(that: PlayerView, event, elt) {
        event.preventDefault();
        jQuery(elt).closest("li").remove();
    });

View.on(
    "." + PlayerView.classname,
    "click",
    "button.repitch",
    function(that: PlayerView, event, elt) {
        event.preventDefault();
        that.repitch();
    });

View.on(
    "." + PlayerView.classname,
    "change",
    "input.cents",
    function(that: PlayerView, event, elt) {
        event.preventDefault();
        const cents = jQuery(elt).val() as number;
        that.setRepitchCents(cents);
    });

View.on(
    "." + PlayerView.classname,
    "click",
    "button.saveRepitch",
    function(that: PlayerView, event, elt) {
        event.preventDefault();
        that.saveRepitch();
    });

View.on(
    "." + PlayerView.classname,
    "click",
    "button.cancelRepitch",
    function(that: PlayerView, event, elt) {
        event.preventDefault();
        that.cancelRepitch();
    });

View.on(
    "." + PlayerView.classname,
    "click",
    "button.activation",
    function(that: PlayerView, event, elt) {
        event.preventDefault();
        const active = jQuery(elt).data("active") ? true : false;
        that.setActivation(active);
    });

View.on(
    "." + PlayerView.classname,
    "click",
    "button.edit-recording",
    function(that: PlayerView, event, elt) {
        event.preventDefault();
        that.editRecording();
    });

View.on(
    "." + PlayerView.classname,
    "input",
    ".leftright",
    function(that: PlayerView, event, elt) {
        event.preventDefault();
        const val = jQuery(elt).val() as any as number;
        that.configLeftRight(val);
    });
